import { tryVendorSlug, type VendorSlug } from '../vendor/vendor-slug';

/**
 * One vendor's listing of a product, read off the `offers` jsonb column (CONTEXT.md "Offer").
 * The array keeps the backend's order: in-stock offers first, ascending by price.
 */
export interface Offer {
  vendor: VendorSlug;
  price: number;
  inStock: boolean;
  promotion: { percent: number; originalPrice: number } | null;
  unitPrice: { value: number; unit: string } | null;
  url: string | null;
}

/**
 * The offers a row's `offers` column holds. An element that breaks an invariant (no vendor,
 * a suffixed vendor key, no positive price, no availability, a vendor already seen) is dropped;
 * anything that is not an array parses to no offers.
 */
export function parseOffers(value: unknown): readonly Offer[] {
  if (!Array.isArray(value)) return [];
  const offers: Offer[] = [];
  const seen = new Set<string>();
  for (const element of value) {
    const offer = parseOffer(element);
    if (offer === null || seen.has(offer.vendor)) continue;
    seen.add(offer.vendor);
    offers.push(offer);
  }
  return offers;
}

function parseOffer(element: unknown): Offer | null {
  if (!isRecord(element)) return null;
  const apoid = asText(element['apoid']);
  const vendor = apoid === null ? null : tryVendorSlug(apoid.toLowerCase());
  const price = asPositiveNumber(element['price']);
  const availability = asText(element['availability']);
  if (vendor === null || price === null || availability === null) return null;

  const percent = asPositiveNumber(element['price_promo_percent']);
  const originalPrice = asPositiveNumber(element['price_original']);
  const unitValue = asPositiveNumber(element['volprice']);
  const unit = asText(element['volunit']);

  return {
    vendor,
    price,
    inStock: availability === 'InStock',
    promotion: percent !== null && originalPrice !== null ? { percent, originalPrice } : null,
    unitPrice: unitValue !== null && unit !== null ? { value: unitValue, unit } : null,
    url: asText(element['url']),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// `volprice` arrives as a numeric string: data-access.md "Schema facts", the offers element.
function asPositiveNumber(value: unknown): number | null {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}
